import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ApiService } from './api.service';
import {
  isImageReportFile,
  normalizeReportAssetUrl,
  readBlobErrorMessage,
  ReportFileRef,
  reportFileApiUrl,
  toTypedBlob,
} from '../../utils/report-file.util';

export type ReportViewerPayload = {
  blobUrl: string;
  isImage: boolean;
  title: string;
};

@Injectable({
  providedIn: 'root',
})
export class ReportViewerService {
  private apiUrl: string;

  constructor(private http: HttpClient, apiService: ApiService) {
    this.apiUrl = apiService.getBaseUrl();
  }

  private getAuthHeaders() {
    const token = localStorage.getItem('jwtToken');
    return {
      headers: new HttpHeaders({
        Authorization: `Bearer ${token}`,
      }),
    };
  }

  loadReport(file: ReportFileRef | null | undefined): Observable<ReportViewerPayload> {
    const reportId = Number(file?.id);
    const title = file?.name || 'Document';
    const isImage = isImageReportFile(file);

    if (!reportId) {
      return throwError(() => new Error('Document is unavailable.'));
    }

    return this.fetchReportBlob(file).pipe(
      map((blob) => ({
        blobUrl: URL.createObjectURL(toTypedBlob(blob, file?.type)),
        isImage,
        title,
      })),
    );
  }

  fetchReportBlob(file: ReportFileRef | null | undefined): Observable<Blob> {
    const reportId = Number(file?.id);
    if (!reportId) {
      return throwError(() => new Error('Document is unavailable.'));
    }

    return this.fetchReportBlobById(reportId).pipe(
      catchError((primaryError) => this.fetchLegacyReportBlob(file).pipe(
        catchError(() => throwError(() => primaryError)),
      )),
    );
  }

  private fetchReportBlobById(reportId: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/reports/${reportId}/file`, {
      ...this.getAuthHeaders(),
      responseType: 'blob',
      observe: 'response',
    }).pipe(
      switchMap((response) => this.ensureBlob(response)),
    );
  }

  private fetchLegacyReportBlob(file: ReportFileRef | null | undefined): Observable<Blob> {
    const candidates = this.legacyReportUrls(file);
    if (!candidates.length) {
      return throwError(() => new Error('Document file could not be found.'));
    }

    return this.fetchFirstAvailableBlob(candidates);
  }

  private legacyReportUrls(file: ReportFileRef | null | undefined): string[] {
    const urls = new Set<string>();
    const reportId = Number(file?.id);

    if (file?.api_url) {
      urls.add(normalizeReportAssetUrl(file.api_url, this.apiUrl));
    }

    if (file?.url) {
      urls.add(normalizeReportAssetUrl(file.url, this.apiUrl));
    }

    if (reportId) {
      urls.add(reportFileApiUrl(reportId, this.apiUrl));
    }

    return Array.from(urls).filter(Boolean);
  }

  private fetchFirstAvailableBlob(urls: string[], index = 0): Observable<Blob> {
    const url = urls[index];
    if (!url) {
      return throwError(() => new Error('Document file could not be found.'));
    }

    return this.http.get(url, {
      ...this.getAuthHeaders(),
      responseType: 'blob',
      observe: 'response',
    }).pipe(
      switchMap((response) => this.ensureBlob(response)),
      catchError((error) => {
        if (index + 1 >= urls.length) {
          return throwError(() => error);
        }
        return this.fetchFirstAvailableBlob(urls, index + 1);
      }),
    );
  }

  private ensureBlob(response: HttpResponse<Blob>): Observable<Blob> {
    const body = response.body;
    const status = response.status;
    const contentType = String(response.headers.get('content-type') || body?.type || '').toLowerCase();

    if (!body?.size || status >= 400) {
      return from(readBlobErrorMessage(body, 'Unable to open document.')).pipe(
        switchMap((message) => throwError(() => new Error(message))),
      );
    }

    if (contentType.includes('application/json') || contentType.includes('text/html')) {
      return from(readBlobErrorMessage(body, 'Unable to open document.')).pipe(
        switchMap((message) => throwError(() => new Error(message))),
      );
    }

    return of(body);
  }
}
